/**
 * LetonKu Courier Web Push Notification Utility
 * Handles VAPID key conversion, browser capabilities detection,
 * Service Worker registration, and Push Subscription creation/deletion.
 */

// Helper to convert base64 URL safe VAPID key to Uint8Array for PushManager
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export interface PushSupportStatus {
  isSupported: boolean;
  permission: NotificationPermission | 'unsupported';
  isIOS: boolean;
  isStandalone: boolean;
  serviceWorkerRegistered: boolean;
}

// Detect browser & device capabilities for Web Push
export function checkPushNotificationSupport(): PushSupportStatus {
  const isSWSupported = 'serviceWorker' in navigator;
  const isPushSupported = 'PushManager' in window;
  const isNotificationSupported = 'Notification' in window;

  const isSupported = isSWSupported && isPushSupported && isNotificationSupported;

  // iOS Safari detection
  const ua = window.navigator.userAgent.toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(ua);
  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as unknown as { standalone?: boolean }).standalone === true;

  const permission = isNotificationSupported ? Notification.permission : 'unsupported';

  return {
    isSupported,
    permission,
    isIOS,
    isStandalone,
    serviceWorkerRegistered: isSWSupported && !!navigator.serviceWorker.controller,
  };
}

// Fetch VAPID Public Key from Express backend
export async function fetchVapidPublicKey(): Promise<string> {
  try {
    const res = await fetch('/api/push/vapid-public-key');
    if (!res.ok) {
      throw new Error(`Failed to fetch VAPID key (Status: ${res.status})`);
    }
    const data = await res.json();
    return data.publicKey || '';
  } catch (err) {
    console.warn('[WebPush Utility] Error fetching VAPID public key from backend:', err);
    return '';
  }
}

// Register or get active Service Worker
export async function getOrRegisterServiceWorker(): Promise<ServiceWorkerRegistration> {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service Worker tidak didukung di browser ini.');
  }

  let registration = await navigator.serviceWorker.getRegistration('/');
  if (!registration) {
    registration = await navigator.serviceWorker.getRegistration();
  }
  if (!registration) {
    registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  }

  // Wait for service worker to become ready
  await navigator.serviceWorker.ready;
  return registration;
}

export interface CourierSubscribeParams {
  courierUsername: string;
  outletId: string;
  role?: string;
  authToken?: string;
}

// Subscribe Courier device to Web Push Notification
export async function subscribeCourierPushNotification(
  params: CourierSubscribeParams
): Promise<{ success: boolean; subscription?: PushSubscription; error?: string }> {
  const support = checkPushNotificationSupport();
  if (!support.isSupported) {
    if (support.isIOS && !support.isStandalone) {
      return {
        success: false,
        error:
          'Pada perangkat iPhone/iOS, aplikasi LetonKu harus ditambahkan ke Utama (Add to Home Screen) terlebih dahulu untuk mengaktifkan Notifikasi Web Push.',
      };
    }
    return {
      success: false,
      error: 'Browser Anda tidak mendukung Web Push Notification API.',
    };
  }

  try {
    // 1. Request user permission explicitly
    const permissionResult = await Notification.requestPermission();
    if (permissionResult !== 'granted') {
      return {
        success: false,
        error: 'Izin notifikasi ditolak oleh pengguna atau browser.',
      };
    }

    // 2. Fetch VAPID Public Key
    const vapidPublicKey = await fetchVapidPublicKey();
    if (!vapidPublicKey) {
      return {
        success: false,
        error: 'Gagal mengambil VAPID Public Key dari server.',
      };
    }

    // 3. Register & await Service Worker
    const registration = await getOrRegisterServiceWorker();

    // 4. Check existing subscription or subscribe
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      const convertedKey = urlBase64ToUint8Array(vapidPublicKey);
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertedKey,
      });
    }

    const subJson = subscription.toJSON();
    const endpoint = subJson.endpoint || subscription.endpoint;
    const p256dh = subJson.keys?.p256dh || '';
    const auth = subJson.keys?.auth || '';

    if (!endpoint || !p256dh || !auth) {
      return {
        success: false,
        error: 'Kunci enkripsi subscription perangkat tidak lengkap.',
      };
    }

    const deviceInfo = `${navigator.platform || 'Web'} • ${navigator.userAgent.slice(0, 80)}`;

    const reqHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (params.authToken) {
      reqHeaders['Authorization'] = `Bearer ${params.authToken.trim()}`;
    }

    // 5. Send subscription details to server
    const response = await fetch('/api/courier/push-subscription', {
      method: 'POST',
      headers: reqHeaders,
      body: JSON.stringify({
        subscription: {
          endpoint,
          keys: {
            p256dh,
            auth,
          },
        },
        username: params.courierUsername,
        outletId: params.outletId,
        role: params.role || 'courier',
        deviceInfo,
      }),
    });

    const resData = await response.json();
    if (!response.ok || !resData.success) {
      throw new Error(resData.error || 'Gagal menyimpan subscription ke server.');
    }

    // Save active state to localStorage
    try {
      localStorage.setItem('leton_courier_push_subscribed', 'true');
      localStorage.setItem('leton_courier_push_endpoint', endpoint);
    } catch {}

    return {
      success: true,
      subscription,
    };
  } catch (err: any) {
    console.error('[WebPush Utility] Error subscribing courier push notification:', err);
    return {
      success: false,
      error: err?.message || 'Terjadi kesalahan saat mengaktifkan notifikasi.',
    };
  }
}

// Unsubscribe Courier device from Web Push Notification
export async function unsubscribeCourierPushNotification(params?: {
  authToken?: string;
}): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    if ('serviceWorker' in navigator) {
      let registration = await navigator.serviceWorker.getRegistration('/');
      if (!registration) {
        registration = await navigator.serviceWorker.getRegistration();
      }
      if (registration) {
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          const endpoint = subscription.endpoint;
          await subscription.unsubscribe();

          const reqHeaders: Record<string, string> = {
            'Content-Type': 'application/json',
          };
          if (params?.authToken) {
            reqHeaders['Authorization'] = `Bearer ${params.authToken.trim()}`;
          }

          // Notify backend
          await fetch('/api/courier/push-subscription', {
            method: 'DELETE',
            headers: reqHeaders,
            body: JSON.stringify({ endpoint }),
          }).catch(() => {});
        }
      }
    }

    try {
      localStorage.removeItem('leton_courier_push_subscribed');
      localStorage.removeItem('leton_courier_push_endpoint');
    } catch {}

    return { success: true };
  } catch (err: any) {
    console.error('[WebPush Utility] Error unsubscribing push notification:', err);
    return {
      success: false,
      error: err?.message || 'Gagal menonaktifkan notifikasi.',
    };
  }
}
